import React from 'react';
import { Icon } from '../Icon/Icon';
import styles from './Alert.module.scss';

export type AlertVariant = 'error' | 'warning' | 'info' | 'success' | 'neutral';

export type AlertProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> & {
  variant?: AlertVariant;
  icon?: string;
  children: React.ReactNode;
  onClose?: () => void;
  'data-testid'?: string;
};

const DEFAULT_ICONS: Record<AlertVariant, string> = {
  error: 'exclamation-circle',
  warning: 'exclamation-triangle',
  info: 'information-circle',
  success: 'check-circle',
  neutral: 'information-circle',
};

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  icon,
  children,
  onClose,
  className,
  'data-testid': dataTestId,
  ...rest
}) => {
  const iconName = icon ?? DEFAULT_ICONS[variant];
  const role = variant === 'error' ? 'alert' : 'status';

  return (
    <div
      role={role}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
      className={`${styles['alert']} ${styles[variant]} ${className ?? ''}`}
      data-testid={dataTestId}
      {...rest}
    >
      <span className={styles['icon']} aria-hidden="true">
        <Icon name={iconName} size={18} />
      </span>

      <span className={styles['content']}>{children}</span>

      {onClose && (
        <button
          type="button"
          className={styles['close']}
          onClick={onClose}
          aria-label="Zavřít"
          data-testid={dataTestId ? `${dataTestId}-close` : undefined}
        >
          <Icon name="x-mark" size={16} />
        </button>
      )}
    </div>
  );
};
